// Debounced auto-push to GitHub. Subscribes to the git-store's
// commit event and schedules a push 30s after the latest commit,
// with a 3-minute max-wait cap so a long chat session still gets
// backed up periodically even while edits keep landing.
//
// Push fires only when (a) the user has flipped autoPush on AND
// (b) token + repo are set in backupStore. A failed push reschedules
// rather than giving up — transient network errors shouldn't strand
// the data.
//
// Wired once from main.tsx. No-op if either condition above is false
// at the time the timer fires.

import { onCommit } from "./git-store";
import { pushSnapshot } from "./backup-sync";
import { useBackupStore } from "../store/backupStore";

const DEBOUNCE_MS = 30_000;
const MAX_WAIT_MS = 180_000;

let pendingTimer: ReturnType<typeof setTimeout> | null = null;
let firstPendingAt: number | null = null;
let inFlight = false;

export function initAutoPush(): void {
  onCommit(() => {
    // Read state at fire-time so toggles take effect immediately.
    const { autoPush, token, repo } = useBackupStore.getState();
    if (!autoPush || !token || !repo) return;
    scheduleFlush();
  });
}

function scheduleFlush(): void {
  if (firstPendingAt === null) firstPendingAt = Date.now();
  if (pendingTimer !== null) clearTimeout(pendingTimer);

  const sinceFirst = Date.now() - firstPendingAt;
  const debounceLeft = DEBOUNCE_MS;
  const maxWaitLeft = MAX_WAIT_MS - sinceFirst;
  // Whichever fires sooner: the rolling 30s window since the latest
  // commit, or the 3-minute cap since the first pending commit.
  const wait = Math.max(0, Math.min(debounceLeft, maxWaitLeft));

  pendingTimer = setTimeout(() => {
    pendingTimer = null;
    firstPendingAt = null;
    void flush();
  }, wait);
}

async function flush(): Promise<void> {
  if (inFlight) {
    // A push is already running — let it finish, then any commits
    // that landed during the flight will re-schedule via onCommit.
    return;
  }
  const { autoPush, token, repo } = useBackupStore.getState();
  if (!autoPush || !token || !repo) return;
  inFlight = true;
  try {
    const result = await pushSnapshot(
      { token, repo },
      `Playground auto-backup — ${new Date().toISOString()}`,
    );
    useBackupStore.getState().setLastPushed(result.oid, Date.now());
    console.info(`[auto-push] backed up ${result.fileCount} file(s) → ${result.oid.slice(0, 7)}`);
  } catch (e) {
    console.warn("[auto-push] push failed, will retry on next commit:", e);
    // Re-schedule so a transient failure doesn't strand the user;
    // pretend this run never happened.
    scheduleFlush();
  } finally {
    inFlight = false;
  }
}

/** Manual push — used by the Backup modal's "Push now" button.
 *  Throws on error so the UI can surface it. */
export async function pushNow(): Promise<{ oid: string; fileCount: number; url: string }> {
  const { token, repo } = useBackupStore.getState();
  if (!token || !repo) throw new Error("Backup not configured — set token + repo first.");
  const result = await pushSnapshot(
    { token, repo },
    `Playground manual backup — ${new Date().toISOString()}`,
  );
  useBackupStore.getState().setLastPushed(result.oid, Date.now());
  return result;
}
